import type { AccountListApiData } from '@navio/nsdk';
import { filterUnitFor, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { USER_ROLE_OPTIONS } from 'noddi-util';
import { ADDRESS_FILTER_TEXT, useAddressOptions } from '../Address/useAddressFilter';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import { CITY_FILTER_TEXT, useCityOptions } from '../Location/useCitiesFilter';
import { ORGANIZATION_FILTER_TEXT, useOrganizationOptions } from '../Organization/useOrganizationFilter';
import {
  SERVICE_DEPARTMENT_FILTER_TEXT,
  useServiceDepartmentOptions
} from '../ServiceDepartment/useServiceDepartmentFilter';
import {
  SERVICE_ORGANIZATION_FILTER_TEXT,
  useServiceOrganizationOptions
} from '../ServiceOrganization/useServiceOrganizationFilter';
import {
  DATA_QUALITY_ISSUE_TYPES_FILTER,
  HAS_DATA_QUALITY_ISSUES_FILTER_TEXT
} from '../UserGroup/useUserGroupDataQualityFilters';

type AccountListFilterInput = Omit<
  NonNullable<AccountListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const accountFilterUnit = filterUnitFor<AccountListFilterInput>();

export const useAccountTableFilters = () => {
  const serviceOrganizationSpec = accountFilterUnit.multiSelect('serviceOrganizationIds', {
    ...SERVICE_ORGANIZATION_FILTER_TEXT,
    source: useServiceOrganizationOptions(),
    exclude: true
  });
  const { serviceOrganizationIds } = useFilterSpecValue(serviceOrganizationSpec);
  const serviceDepartmentSpec = accountFilterUnit.multiSelect('serviceDepartmentIds', {
    ...SERVICE_DEPARTMENT_FILTER_TEXT,
    source: useServiceDepartmentOptions({ serviceOrganizationIds }),
    exclude: true
  });
  const { serviceDepartmentIds } = useFilterSpecValue(serviceDepartmentSpec);
  const citySpec = accountFilterUnit.multiSelect('cities', {
    ...CITY_FILTER_TEXT,
    source: useCityOptions(),
    exclude: true
  });
  const { cities } = useFilterSpecValue(citySpec);

  return useFilterUnits([
    accountFilterUnit.boolean('hasDataQualityIssues', {
      ...HAS_DATA_QUALITY_ISSUES_FILTER_TEXT,
      description: 'Show accounts with or without booking-critical data quality issues on their customers.'
    }),
    accountFilterUnit.multiSelect('dataQualityIssueTypes', {
      ...DATA_QUALITY_ISSUE_TYPES_FILTER,
      description: 'Show accounts belonging to customers with these data quality issues.',
      exclude: true
    }),
    accountFilterUnit.multiSelect('roles', { title: 'Roles', options: USER_ROLE_OPTIONS, exclude: true }),
    accountFilterUnit.boolean('emailVerified', {
      title: 'Email verified',
      description: 'Show accounts whose current email is verified.'
    }),
    accountFilterUnit.boolean('phoneNumberVerified', {
      title: 'Phone verified',
      description: 'Show accounts whose current phone number is verified.'
    }),
    accountFilterUnit.dateRange('phoneChangedAt', {
      title: 'Phone changed',
      description: 'Phone number changed in this date range.',
      exclude: true
    }),
    accountFilterUnit.dateRange('emailChangedAt', {
      title: 'Email changed',
      description: 'Email changed in this date range.',
      exclude: true
    }),
    accountFilterUnit.dateRange('nameChangedAt', {
      title: 'Name changed',
      description: 'First or last name changed in this date range.',
      exclude: true
    }),
    serviceDepartmentSpec,
    serviceOrganizationSpec,
    accountFilterUnit.multiSelect('organizationIds', {
      ...ORGANIZATION_FILTER_TEXT,
      source: useOrganizationOptions({ serviceDepartmentIds }),
      exclude: true
    }),
    accountFilterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
    accountFilterUnit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true }),
    citySpec,
    accountFilterUnit.multiSelect('addressIds', {
      ...ADDRESS_FILTER_TEXT,
      source: useAddressOptions({ serviceDepartmentIds, cities }),
      exclude: true
    })
  ]);
};
